import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Flex,
  Icon,
  // Chakra Popover is used here (not @kula-ai/components) because we need a
  // controlled, trigger-less announcement anchored to the Settings item with
  // a one-time dismiss — kula-ai's Popover is hover/click-trigger oriented.
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
} from '@chakra-ui/react';
import { XClose } from '@kula-ai/icons-core';
import { NavLink } from './NavLink';
import { LayoutAlt02 } from '@kula-ai/icons/dual-tone/LayoutAlt02';
import { KText } from '@/ui-library/components';
import NavigationContext from '../context';

// One-time announcement: tells users that Flow templates moved into
// Settings > Templates. Persisted via localStorage so it shows once and never
// again after the user dismisses it.
const VIEWED_KEY = 'viewed_unified_templates';

interface SettingsNavLinkProps {
  icon: React.ElementType;
  link: string;
  title: string;
  testID?: string;
  isCollapsed?: boolean;
  // Only ATS users have the Settings > Templates destination, so the nudge is
  // gated to them. Falls back to a plain NavLink otherwise.
  nudgeEnabled?: boolean;
}

export const SettingsNavLink = ({
  nudgeEnabled = false,
  ...navLinkProps
}: SettingsNavLinkProps) => {
  const history = useHistory();
  // Show until the user has dismissed it (flag === 'true'). Read once on mount.
  const [showNudge, setShowNudge] = useState(
    () => nudgeEnabled && localStorage.getItem(VIEWED_KEY) !== 'true'
  );
  const { isNavCollapsed } = useContext(NavigationContext) ?? {};
  const isFirstCollapseRun = useRef(true);

  useEffect(() => {
    // First visit: seed the flag as 'false' so it's explicitly tracked.
    if (nudgeEnabled && localStorage.getItem(VIEWED_KEY) === null) {
      localStorage.setItem(VIEWED_KEY, 'false');
    }
  }, [nudgeEnabled]);

  // The sidebar animates its width when collapsing/expanding (~0.25s), which
  // moves the Settings anchor. Chakra's Popover (Popper) only repositions on
  // scroll/window-resize, so on its own it won't track the anchor mid-
  // transition — the card would snap at the end. On each toggle, pump resize
  // events across the transition window so Popper recomputes every frame and
  // the popover glides with the sidebar.
  useEffect(() => {
    if (!showNudge) return undefined;
    if (isFirstCollapseRun.current) {
      isFirstCollapseRun.current = false;
      return undefined;
    }
    let rafId = 0;
    let start: number | null = null;
    const DURATION = 320; // sidebar width transition (250ms) + small buffer
    const tick = (ts: number) => {
      if (start === null) start = ts;
      window.dispatchEvent(new Event('resize'));
      if (ts - start < DURATION) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isNavCollapsed, showNudge]);

  const dismissNudge = () => {
    localStorage.setItem(VIEWED_KEY, 'true');
    setShowNudge(false);
  };

  if (!nudgeEnabled) {
    return <NavLink {...navLinkProps} />;
  }

  const goToTemplates = () => {
    dismissNudge();
    history.push('/settings/templates?tab=flow');
  };

  return (
    <Popover
      isOpen={showNudge}
      placement="right"
      closeOnBlur={false}
      autoFocus={false}
      returnFocusOnClose={false}
      gutter={12}
      // Keep a 12px gap from the viewport edges so the card never sticks to
      // the bottom of the page.
      modifiers={[{ name: 'preventOverflow', options: { padding: 12 } }]}
    >
      <PopoverAnchor>
        <Box>
          <NavLink {...navLinkProps} />
        </Box>
      </PopoverAnchor>
      <PopoverContent
        width="420px"
        bg="primary-25"
        border="1px solid"
        borderColor="primary-400"
        borderRadius="6px"
        boxShadow="0px 12px 32px rgba(38, 34, 68, 0.12)"
        _focus={{
          outline: 'none',
          boxShadow: '0px 12px 32px rgba(38, 34, 68, 0.12)',
        }}
        _focusVisible={{ outline: 'none' }}
        // Speech-bubble tail on the left edge pointing at the Settings gear.
        // Chakra's <PopoverArrow> only renders a 4-sided diamond, so we draw
        // the tail ourselves: a rotated square whose two outer edges carry the
        // primary-400 border while its body (primary-25) masks the card's left
        // border where the tail joins.
        _before={{
          content: '""',
          position: 'absolute',
          left: '-6px',
          bottom: '24px',
          width: '12px',
          height: '12px',
          bg: 'primary-25',
          borderLeft: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'primary-400',
          transform: 'rotate(45deg)',
        }}
      >
        <PopoverBody p="12px">
          <Flex justifyContent="space-between" alignItems="flex-start" mb="8px">
            <Flex alignItems="center" gap="8px">
              <Icon as={LayoutAlt02} boxSize="18px" color="secondary-700" />
              <KText
                fontSize="14px"
                fontWeight="medium"
                color="secondary-900"
                lineHeight="20px"
              >
                Flow templates has a new home
              </KText>
            </Flex>
            <Icon
              as={XClose}
              boxSize="16px"
              color="secondary-900"
              cursor="pointer"
              flexShrink={0}
              onClick={dismissNudge}
              _hover={{ color: 'secondary-900' }}
              data-test-id="unified-templates-nudge-close"
            />
          </Flex>
          <KText
            fontSize="13px"
            fontWeight="regular"
            color="secondary-700"
            lineHeight="20px"
            mb="8px"
          >
            Flow templates now live in{' '}
            <KText as="span" fontWeight="semiBold">
              Settings
            </KText>
            , under{' '}
            <KText as="span" fontWeight="semiBold">
              Communication templates
            </KText>
            . Find it under the new{' '}
            <KText as="span" fontWeight="semiBold">
              For flows
            </KText>{' '}
            tab.
          </KText>
          <KText
            as="button"
            fontSize="13px"
            fontWeight="semiBold"
            color="primary-600"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={goToTemplates}
            data-test-id="unified-templates-nudge-link"
          >
            Go to Settings {'>'} Communication templates
          </KText>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
